import { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { CodeBlock } from "react-code-blocks";
import "./DrawerRight.scss";
import { getCodeBlocks, CodeBlocks } from "./CodeBlocks";
import { useParams } from "react-router-dom";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import React from "react";
import { useLocation } from "react-router-dom";
import {
  AsyncThunkAction,
  ThunkDispatch,
  UnknownAction,
} from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { setEnvironment } from "../../redux/slice/RegisterAppSlice";

interface PermanentDrawerProps {
  codeurl: string;
  languageurl: string;
  rawHeaders: any;
  activeRoute: string;
}

interface ListItemContent {
  clientId?: number;
  clientName?: string;
  sM_Logo_Path?: string;
  lG_Logo_Path?: string;
  payerID_Stage?: string;
  payerID_Prod?: string;
  text: string;
  sandboxId: string;
  productionId: string;
  src: string;
}

export const PermanentDrawerRight = (props: PermanentDrawerProps) => {
  const location = useLocation();
  const { route, env } = location.state || {};
  const [headers, setHeaders]: any = useState(``);
  const [isCopied, setIsCopied] = useState(false);
  const [tooltipText, setTooltipText] = useState("Copy");
  const [tooltipTextCode, setTooltipTextCode] = useState("Copy");
  const [isCopiedCode, setIsCopiedCode] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(
    "ruby"
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const dispatch = useDispatch<AppDispatch>();
  const clientConfigurations = useSelector(
    (state: any) => state?.registerApp?.configurations
  );
  const filteredConfigurations = clientConfigurations?.filter(
    (item: any) => item.isAction !== 0 && !item.isActive
  );

  const openMenu = Boolean(anchorEl);

  const { environment } = useParams<{ environment?: string }>();
  const [isProduction, setProduction] = useState<boolean>(
    environment === "prod"
  );

  const [selectedId, setSelectedId] = useState<any>(getSelectedPayerId(route));

  const [selectedPayer, setSelectedPayer] = useState<ListItemContent | null>(
    getInitialPayer(route)
  );

  const handleClick = (event: React.MouseEvent<any>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  function getInitialPayer(route: string | undefined): ListItemContent | null {
    if (!route?.length) return filteredConfigurations?.[0];
    if (isProduction) {
      return filteredConfigurations?.find(
        (x: ListItemContent) => x.clientName === route
      );
    }
    return filteredConfigurations?.find(
      (x: ListItemContent) => x.clientName === route
    );
  }

  function getSelectedPayerId(
    route: string | undefined
  ): ListItemContent | null {
    if (!route?.length) return filteredConfigurations?.[0]?.payerID_Stage;
    if (isProduction) {
      return filteredConfigurations?.find(
        (x: ListItemContent) => x.clientName === route
      )?.payerID_Prod;
    }
    return filteredConfigurations?.find(
      (x: ListItemContent) => x.clientName === route
    )?.payerID_Stage;
  }

  const handleRadioChange = (id: string) => {
    setSelectedId(id);
    // Find and set the corresponding payer based on the selected id
    const payer = clientConfigurations.find(
      (item: ListItemContent) =>
        item.payerID_Stage === id || item.payerID_Prod === id
    );
    if (payer) setSelectedPayer(payer);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleHeaders = () => {
    const data: any = props.rawHeaders;
    let strQuery = ``;

    Object.keys(data).forEach((each: string, index: number) => {
      if (data[each]) {
        if (selectedLanguage === "ruby")
          strQuery += ` \n request["${each}"] = "${data[each]}" ${","}`;

        if (selectedLanguage === "java")
          strQuery += `  \n .headers("${each}", "${data[each]}")`;

        if (selectedLanguage === "python")
          strQuery += `  \n "${each}": "${data[each]}"`;
        if (selectedLanguage === "javascript")
          strQuery += `  \n '${each}': '${data[each]}'`;

        if (selectedLanguage === "php")
          strQuery += `  \n "header => ${each}:${data[each]}"`;
      }
    });
    if (selectedLanguage) {
      setHeaders(strQuery);
    }
  };

  useEffect(() => {
    const payer = clientConfigurations?.find(
      (item: ListItemContent) =>
        item?.payerID_Stage === selectedId || item?.payerID_Prod === selectedId
    );
    setSelectedIndex(isProduction ? 1 : 0);
    setSelectedId(isProduction ? payer?.payerID_Prod : payer?.payerID_Stage);
    dispatch(setEnvironment(isProduction ? "production" : "sandbox"));
    handleHeaders();
  }, [isProduction]);

  useEffect(() => {
    if (!selectedId || !selectedPayer) {
      setSelectedId(clientConfigurations?.[0]?.payerID_Stage);
      setSelectedPayer(clientConfigurations?.[0]);
    }
  }, [selectedId, selectedPayer, clientConfigurations]);

  const codeBlocks: CodeBlocks = getCodeBlocks(
    props.codeurl,
    props.languageurl,
    headers
  ); 

  const languages = [
    {
      label: "Ruby",
      avatarSrc: "/svg/icons8-ruby-programming-language.svg",
      language: "ruby",
    },
    { label: "Python", avatarSrc: "/svg/python.svg", language: "python" },
    { label: "Java", avatarSrc: "/svg/java-original.svg", language: "java" },
    { label: "PHP", avatarSrc: "/svg/php-plain.svg", language: "php" },
    { label: "Node", avatarSrc: "/svg/node-js.svg", language: "javascript" },
    {
      label: "C Sharp",
      avatarSrc: "/svg/c-sharp-14369.svg",
      language: "csharp",
    },
  ];

  const handleLanguageSelect = (language: string) => {
    setSelectedLanguage(language);
    setAnchorEl(null); // Close the menu after selecting a language
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleCopyClick = async (event: React.MouseEvent<any>) => {
    event.stopPropagation();
    setTooltipText("ID Copied");
    setIsCopied(true);
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(selectedId.toLowerCase());
      } catch {}
    }
    setTimeout(() => {
      setTooltipText("Copy");
      setIsCopied(false);
    }, 3000);
  };
  const handleCopyCode = async (event: React.MouseEvent<any>) => {
    event.stopPropagation();
    setTooltipTextCode("Code Copied");
    setIsCopiedCode(true);
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(
          codeBlocks[selectedLanguage || "javascript"]
        );
      } catch {}
    }
    setTimeout(() => {
      setTooltipTextCode("Copy");
      setIsCopiedCode(false);
    }, 3000);
  };
  const [selectedIndex, setSelectedIndex] = useState<number | null>(0);

  const handleButtonClick = (index: number) => {
    setSelectedIndex(index);
    console.log("selected index is====>",index)
    const env = index === 0 ? "sandbox" : "production";
    setProduction(env === "production");
    dispatch(setEnvironment(env)); // 👈 update Redux
  };

  const buttons = ["Sandbox", "Production"];
  return (
    <Box sx={{ display: "flex" }}>
      <Drawer
        className="right-panel"
        sx={{
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
          },
        }}
        variant="permanent"
        anchor="right"
      >
        <section className="language-select">
          <ButtonGroup
            variant="contained"
            aria-label="Button group"
            className="custom-btn-group"
            fullWidth
          >
            {buttons.map((label, index) => (
              <Button
                key={index}
                fullWidth
                onClick={() => handleButtonClick(index)}
                className={selectedIndex === index ? "selected" : ""}
              >
                {label === "Sandbox" && <i className="ac_icons">ac_union</i>}
                {label === "Production" && (
                  <i className="ac_icons">ac_double_settings</i>
                )}
                {label}
              </Button>
            ))}
          </ButtonGroup>
          <Button
            className="client-info"
            sx={{ p: 0 }}
            id="basic-button"
            aria-controls={openMenu ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={openMenu ? "true" : undefined}
            onClick={handleClick}
            disableRipple
          >
            <List sx={{ p: 0 }}>
              <ListItem sx={{ p: 0 }} className="list-box">
                <Box className="content-block">
                  <ListItemAvatar sx={{ p: 0 }}>
                    <Avatar className="avatar">
                      {selectedPayer && (
                        <img
                          src={selectedPayer?.sM_Logo_Path}
                          alt="clientInfo"
                        />
                      )}
                    </Avatar>
                  </ListItemAvatar>
                  <div className="text-info">
                    <div className="list-text-header">Select Payer</div>
                    {selectedPayer && (
                      <div className="list-text-client">
                        {selectedPayer.clientName ||
                          clientConfigurations[0]?.clientName ||
                          "Select Payer"}
                      </div>
                    )}
                    <div className="other-info">
                      {selectedPayer && (
                        <>
                          <div
                            className={
                              selectedIndex === 0 ? "sandbox" : "production"
                            }
                          >
                            {selectedIndex === 0 ? "Sandbox" : "Production"}
                          </div>
                          <Box className="client-id">{selectedId}</Box>
                        </>
                      )}
                    </div>
                  </div>
                </Box>
                <div>
                  <KeyboardArrowDownOutlinedIcon
                    className="arrow-down"
                    style={{
                      transform: openMenu ? "rotate(-180deg)" : "rotate(0deg)",
                      transition: "transform 0.3s ease",
                    }}
                  />
                </div>
                <Tooltip title={tooltipText} placement="top" arrow>
                  <span>
                    <IconButton
                      aria-label="Copy"
                      onClick={handleCopyClick}
                      disabled={isCopied}
                      className="copy-btn"
                    >
                      <i className="ac_icons">ac_copy</i>
                    </IconButton>
                  </span>
                </Tooltip>
              </ListItem>
            </List>
          </Button>
          <Divider sx={{ mt: 3, mb: 2 }} />
          <Menu
            anchorEl={anchorEl}
            open={openMenu}
            className="custom-sandbox"
            onClose={handleClose}
            transformOrigin={{ horizontal: "center", vertical: "top" }}
            anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
          >
            <MenuItem className="custom-menu">
              <List
                component="nav"
                aria-label="main mailbox folders"
                className="custom-list"
              >
                {clientConfigurations
                  ?.filter((item: any) => item.isAction !== 0 && !item.isActive) // Filter out items with isAction = 0 and isActive = true
                  .map((item: any, index: any) => (
                    <ListItem
                      key={index}
                      className={`custom-list-item ${
                        selectedId === item.payerID_Stage ||
                        selectedId === item.payerID_Prod
                          ? "selected"
                          : ""
                      }`}
                      onClick={() => {
                        handleRadioChange(
                          isProduction ? item.payerID_Prod : item.payerID_Stage
                        );
                        handleClose();
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <ListItemAvatar sx={{ p: 0 }}>
                          <Avatar className="avatar">
                            <img src={item.sM_Logo_Path} alt="clientInfo" />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText>
                          {/* <div className="avatar-text">{item.text}</div> */}
                          <div className="avatar-text">
                            {item.clientName || "Unknown Payer"}
                          </div>
                          <Box sx={{ display: "block" }}>
                            <RadioGroup
                              value={selectedId}
                              aria-labelledby="demo-radio-buttons-group-label"
                              name="radio-buttons-group"
                              className="custom-radio"
                            >
                              {!isProduction && (
                                <FormControlLabel
                                  value={item.payerID_Stage}
                                  control={<Radio className="hide-radio" />}
                                  label={
                                    <Typography variant="body1">
                                      <span className="info-box sandbox">
                                        Sandbox
                                      </span>
                                      <span className="radio-txt">
                                        {item.payerID_Stage}
                                      </span>
                                    </Typography>
                                  }
                                />
                              )}
                              {isProduction && (
                                <FormControlLabel
                                  value={item.payerID_Prod}
                                  control={<Radio className="hide-radio" />}
                                  label={
                                    <Typography variant="body1">
                                      <span className="info-box production">
                                        Production
                                      </span>
                                      <span className="radio-txt">
                                        {item.payerID_Prod}
                                      </span>
                                    </Typography>
                                  }
                                />
                              )}
                            </RadioGroup>
                          </Box>
                        </ListItemText>
                      </Box>
                    </ListItem>
                  ))}
              </List>
            </MenuItem>
          </Menu>

          <div className="header-info">Select Language</div>
          <Box
            sx={{ display: "flex", flexDirection: "row" }}
            className="chips-box"
          >
            {languages.slice(0, 6).map((lang, index) => (
              <div key={index} className="chips-section">
                <Chip
                  variant="outlined"
                  color={
                    selectedLanguage === lang.language ? "primary" : "default"
                  }
                  avatar={<Avatar src={lang.avatarSrc} />}
                  label={lang.label}
                  onClick={() => handleLanguageSelect(lang.language)}
                />
                <div className="label-name">{lang.label}</div>
              </div>
            ))}
            {languages.length > 6 && (
              <div className="chips-section">
                <Chip
                  variant="outlined"
                  label="More"
                  color="default"
                  avatar={
                    <Avatar
                      sx={{ transform: "rotate(90deg)" }}
                      src={"/svg/more_horiz_FILL0_wght400_GRAD0_opsz24.svg"}
                    />
                  }
                  onClick={handleMenuOpen}
                />
                <div className="label-name">More</div>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  {languages.slice(6).map((lang, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => handleLanguageSelect(lang.language)}
                    >
                      {lang.label}
                    </MenuItem>
                  ))}
                </Menu>
              </div>
            )}
          </Box>
          <Card className="code-generator">
            <CardHeader
              action={
                <Tooltip title={tooltipTextCode} placement="top" arrow>
                  <span>
                    <Button
                      aria-label="Copy"
                      onClick={handleCopyCode}
                      disabled={isCopiedCode}
                      className="copy-btn"
                    >
                      <i className="ac_icons">ac_copy</i>
                      <span>Copy</span>
                    </Button>
                  </span>
                </Tooltip>
              }
              title="Request"
            />
            <CardContent>
              <CodeBlock
                text={codeBlocks[selectedLanguage || "ruby"]}
                language={selectedLanguage || "ruby"}
                showLineNumbers={true}
              />
            </CardContent>
          </Card>
          <p className="info">
            As part of the CMS Interoperability and Patient Access Final Rule
            (CMS-9115-F), payers must make provider information available. These
            APIs are required to retrieve the provider information.
          </p>
        </section>
      </Drawer>
    </Box>
  );
};
